// AI-ASSISTED: generated with claude-code on 2026-08-17, reviewed by Yoann Hertienne
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useEntity } from '@backstage/plugin-catalog-react';
import { TileBoundary } from '../team-landing/TileBoundary';

// Per-environment deployed-app URLs live under the `luminus.be/` annotation
// prefix (DPT-505). Teams add whichever environments exist; absent ones are
// simply not rendered. Order follows the canonical Luminus DEV → TST → ACC →
// PRD progression.
const ENVIRONMENTS: ReadonlyArray<{ annotation: string; label: string }> = [
  { annotation: 'luminus.be/url-dev', label: 'DEV' },
  { annotation: 'luminus.be/url-tst', label: 'TST' },
  { annotation: 'luminus.be/url-acc', label: 'ACC' },
  { annotation: 'luminus.be/url-prd', label: 'PRD' },
];

const SWAGGER_ANNOTATION = 'luminus.be/swagger-url';

const EnvironmentUrlsCardBody = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  const rows = ENVIRONMENTS.map(env => ({
    ...env,
    url: annotations[env.annotation],
  })).filter((env): env is typeof env & { url: string } => Boolean(env.url));

  // Swagger / OpenAPI is a service concern only (spec §Approach): render the
  // chip for `service` components that declare the annotation, nowhere else.
  const swaggerUrl = annotations[SWAGGER_ANNOTATION];
  const showSwagger = Boolean(swaggerUrl) && entity.spec?.type === 'service';

  // Hide the card entirely when the component declares no environment URLs and
  // no (applicable) Swagger link — truthful "no data" (spec FR-008).
  if (rows.length === 0 && !showSwagger) return null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Environments</Typography>
          {showSwagger && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              clickable
              label="Swagger"
              component="a"
              href={swaggerUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Open Swagger / OpenAPI docs (opens in a new tab)"
            />
          )}
        </Stack>
        {rows.length > 0 ? (
          <Table size="small" aria-label="Environment URLs">
            <TableBody>
              {rows.map(env => (
                <TableRow key={env.annotation}>
                  <TableCell component="th" scope="row" sx={{ width: '4rem' }}>
                    {env.label}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      clickable
                      label={env.url}
                      component="a"
                      href={env.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${env.label} environment (opens in a new tab)`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            No environment URLs configured.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export const EnvironmentUrlsCard = () => (
  <TileBoundary tool="Environment URLs">
    <EnvironmentUrlsCardBody />
  </TileBoundary>
);
